/**
 * Per-browser history of recent successful calculations. Powers the
 * "Recent" popover in the result panel: tap a row to restore the form
 * to that calc. Stored as full UiValues snapshots so restoration is
 * exact — sizes, locations, mode, discount, all of it.
 *
 * Scope: client-side only, no backend. Matches the lightweight
 * persistence stance in Phase 4a (auth state is the heavy thing; ops
 * memory stays in localStorage).
 */

import type { UiValues } from './_shared';

const KEY = 'fsp.calc.recents.v1';
const MAX = 10;

export type CalcRecentSummary = {
  label: string;
  perUnitSell: number;
  orderTotal: number;
  marginPct: number;
  totalQty: number;
  mode: 'b2c' | 'b2b';
};

export type CalcRecent = {
  /** Epoch ms at save time, used for "2m ago" formatting. */
  at: number;
  /** Full form snapshot — pasted back into react-hook-form on restore. */
  values: UiValues;
  /** Denormalised headline numbers so the popover doesn't re-fetch. */
  summary: CalcRecentSummary;
};

export function loadCalcRecents(): CalcRecent[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isCalcRecent).slice(0, MAX);
  } catch {
    return [];
  }
}

/**
 * Prepend a new entry. Dedupes against the most recent one by comparing
 * values — mid-quote a single field change produces a burst of saves
 * and we don't want each keystroke in the history. Caps at MAX.
 */
export function pushCalcRecent(entry: CalcRecent): CalcRecent[] {
  const existing = loadCalcRecents();
  const prev = existing[0];
  if (prev && valuesEqual(prev.values, entry.values)) {
    // Same inputs — refresh the timestamp but don't add a row.
    const refreshed: CalcRecent[] = [
      { ...prev, at: entry.at, summary: entry.summary },
      ...existing.slice(1),
    ];
    writeAll(refreshed);
    return refreshed;
  }
  const next = [entry, ...existing].slice(0, MAX);
  writeAll(next);
  return next;
}

export function clearCalcRecents(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // noop
  }
}

function writeAll(list: CalcRecent[]): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    // Storage unavailable (private mode / quota). Popover still works
    // in-memory; we just won't have history across reloads.
  }
}

function isCalcRecent(v: unknown): v is CalcRecent {
  if (!v || typeof v !== 'object') return false;
  const r = v as CalcRecent;
  return (
    typeof r.at === 'number' &&
    !!r.values &&
    typeof r.values === 'object' &&
    !!r.summary &&
    typeof r.summary === 'object' &&
    typeof r.summary.label === 'string' &&
    typeof r.summary.perUnitSell === 'number'
  );
}

/**
 * Deep equality on UiValues. Stringify is fine — the object is flat,
 * ~20 numeric/string fields, no cycles.
 */
function valuesEqual(a: UiValues, b: UiValues): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * "2m ago" / "1h ago" / "Yesterday" / "Mar 14". Kept short so it fits
 * the popover row without wrapping.
 */
export function formatAge(atMs: number, now: number = Date.now()): string {
  const secs = Math.max(0, Math.floor((now - atMs) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(atMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
